"use client";

import type { ReactNode } from "react";
import { AuthShell, GlassSurface } from "@akili/auth-kit";
import { cn } from "@/lib/utils";
import { V2_STAGE } from "./classes";
import { V2Footer } from "./v2-footer";
import { V2Nav } from "./v2-nav";
import { V2TrustRow } from "./v2-trust-row";

/**
 * The content column's maximum width in the `centered` variant. `AuthShell`
 * caps it at 440px (the sign-in card); the welcome hero and the plans grid
 * need more, so the cap is lifted on `<main>`'s content wrapper.
 */
const CENTERED_WIDTH = {
  narrow: "",
  medium: "[&>main>div]:max-w-[640px]",
  wide: "[&>main>div]:max-w-[1160px]",
} as const;

export type V2PageWidth = keyof typeof CENTERED_WIDTH;

export interface V2PageProps {
  /** `split`: statement left, card right. `centered`: one column, statement above the card. */
  variant?: "split" | "centered";
  /** The nav's actions (between the wordmark and the theme toggle). */
  actions?: ReactNode;
  /** The statement — usually a `V2ProofPane`. Left column when split, above the card when centered. */
  aside?: ReactNode;
  /** The card's content, or the whole column when `card` is false. */
  children: ReactNode;
  /** Wrap `children` in the frosted card. False for full-width layouts (the plans grid). */
  card?: boolean;
  /** Centered only: the column's width cap. `narrow` = the sign-in card's 440px. */
  width?: V2PageWidth;
  /** Assurances under the card. */
  trust?: { items: readonly string[]; label: string };
  cardClassName?: string;
  cardTestId?: string;
}

/**
 * The V2 page frame: `AuthShell` on the V2 stage with `V2Nav` and `V2Footer`.
 * Light and dark come from the auth tokens and the stage's `dark:` gradient.
 */
export function V2Page({
  variant = "centered",
  actions,
  aside,
  children,
  card = true,
  width = "narrow",
  trust,
  cardClassName,
  cardTestId = "v2-glass-card",
}: V2PageProps) {
  const body = card ? (
    <GlassSurface className={cn("px-7 py-8 sm:px-9 sm:py-10", cardClassName)} data-testid={cardTestId}>
      {children}
    </GlassSurface>
  ) : (
    children
  );

  return (
    <AuthShell
      variant={variant}
      nav={<V2Nav actions={actions} />}
      aside={variant === "split" ? aside : undefined}
      footer={<V2Footer />}
      className={cn(V2_STAGE, variant === "centered" && CENTERED_WIDTH[width])}
    >
      {variant === "centered" && aside ? <div className="mb-8">{aside}</div> : null}
      {body}
      {trust && trust.items.length > 0 ? <V2TrustRow items={trust.items} label={trust.label} /> : null}
    </AuthShell>
  );
}
